import { useState } from "react";
import { useGroups } from "@/lib/hooks/useGroups";
import { useGroupMetrics } from "@/lib/hooks/useGroupMetrics";
import {
  useCreateTelegramCopyRoute,
  useDeleteTelegramCopyRoute,
  useTelegramCopyRoutes,
  useUpdateTelegramCopyRouteStatus,
} from "@/lib/hooks/useTelegramCopyRoutes";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  PauseCircle,
  ArrowRight,
  Copy,
  Trash2,
  MousePointerClick,
} from "lucide-react";
import type { TelegramCopyRoute, TelegramGroup } from "@/lib/types";
import { PageHeader } from "@/components/PageHeader";
import { ForwardingTab } from "@/components/forwarding/ForwardingTab";
import type { Period } from "@/lib/period";

// ---------------------------------------------------------------------------
// Opcoes de periodo
// ---------------------------------------------------------------------------

const PERIOD_LABELS: Record<Period, string> = {
  today: "Hoje",
  "7d": "7 dias",
  "30d": "30 dias",
};

const PERIODS: Period[] = ["today", "7d", "30d"];

// ---------------------------------------------------------------------------
// GruposPage: encaminhamento (contas conectadas), saude e bot nos grupos
// ---------------------------------------------------------------------------

type GruposTab = "config" | "health" | "bot";

const TABS: ReadonlyArray<{ value: GruposTab; label: string }> = [
  { value: "config", label: "Encaminhamento" },
  { value: "health", label: "Saúde" },
  { value: "bot", label: "Bot nos meus grupos" },
];

export function GruposPage(): JSX.Element {
  const [tab, setTab] = useState<GruposTab>("config");
  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader
        title="Grupos e canais"
        subtitle="Escolha de onde vêm as ofertas, para onde vão e como o texto sai."
      />
      <div role="tablist" aria-label="Seções de grupos e canais" className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === t.value
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === "bot" ? <BotGroupsSection /> : <ForwardingTab view={tab} />}
      </div>
    </div>
  );
}

function BotGroupsSection(): JSX.Element {
  const [period, setPeriod] = useState<Period>("30d");
  const { data: groups, isLoading, error } = useGroups();
  const { data: metricsData = [] } = useGroupMetrics(period);

  const count = groups?.length ?? 0;

  // Mapa de metricas por group_id para acesso O(1)
  const metricsMap = new Map(metricsData.map((m) => [m.groupId, m]));

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Cabecalho da secao (o h1 e o da pagina) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Grupos do Telegram em que o @LucraLinkBot é administrador e troca os links.
        </p>
        <div className="flex items-center gap-2">
          <>
            <PeriodSelector value={period} onChange={setPeriod} />
            {!isLoading && !error && count > 0 && (
              <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                {count} {count === 1 ? "grupo" : "grupos"}
              </div>
            )}
          </>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="shrink-0" />
          <span>Erro ao carregar grupos: {error.message}</span>
        </div>
      )}

      {/* Skeleton */}
      {isLoading && (
        <div className="space-y-3" aria-busy="true" aria-label="Carregando grupos">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-[72px] w-full animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && count === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-12 text-center space-y-5">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Users size={24} className="text-primary" />
          </div>

          <div className="space-y-2 max-w-md mx-auto">
            <p className="font-semibold text-foreground">Nenhum grupo conectado ainda</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Para começar, adicione o bot{" "}
              <strong className="text-foreground">@LucraLinkBot</strong> ao seu grupo
              do Telegram como <strong className="text-foreground">administrador</strong>.
              Após adicionado, o grupo aparece aqui automaticamente.
            </p>
          </div>

          <div className="inline-flex flex-col gap-2.5 text-left bg-[hsl(var(--surface-raised)/0.6)] rounded-lg px-5 py-4 text-sm text-muted-foreground max-w-sm mx-auto w-full">
            <p className="font-semibold text-foreground text-xs uppercase tracking-widest mb-0.5">
              Como conectar
            </p>
            <StepItem n={1}>Abra seu grupo no Telegram</StepItem>
            <StepItem n={2}>Vá em Configurações e Administradores</StepItem>
            <StepItem n={3}>Adicione @LucraLinkBot como administrador</StepItem>
            <StepItem n={4}>O grupo aparece aqui em alguns segundos</StepItem>
          </div>
        </div>
      )}

      {/* Lista de grupos com métricas */}
      {!isLoading && !error && count > 0 && (
        <div className="space-y-2.5">
          {groups?.map((group) => {
            const metrics = metricsMap.get(group.id);
            return (
              <GroupCard
                key={group.id}
                group={group}
                clicks={metrics?.clicks ?? 0}
                conversions={metrics?.conversions ?? 0}
              />
            );
          })}
        </div>
      )}

      {!isLoading && !error && count > 0 && (
        <CopyRoutesSection groups={groups ?? []} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GroupCard com mini-métricas (Item 5)
// ---------------------------------------------------------------------------

interface GroupCardProps {
  group: TelegramGroup;
  clicks: number;
  conversions: number;
}

function GroupCard({ group, clicks, conversions }: GroupCardProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-4 rounded-xl border border-border bg-card px-5 py-4",
        "hover:border-primary/25 hover:bg-[hsl(var(--surface-raised)/0.5)] transition-all",
      )}
    >
      {/* Ícone de status */}
      <div className="shrink-0" aria-hidden="true">
        <StatusIcon status={group.status} />
      </div>

      {/* Info principal */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground truncate">
          {group.title ?? "Grupo sem nome"}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 font-mono">
          ID: {group.telegram_chat_id}
        </p>

        {/* Mini-métricas (Item 5) */}
        <div className="flex items-center gap-3 mt-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <MousePointerClick size={11} className="text-[hsl(var(--primary)/0.7)]" aria-hidden="true" />
            <span className="font-mono tabular-nums text-foreground">{clicks}</span>
            <span>cliques</span>
          </span>
          <span className="text-muted-foreground/40 text-[10px]">·</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <CheckCircle2 size={11} className="text-[hsl(var(--primary)/0.7)]" aria-hidden="true" />
            <span className="font-mono tabular-nums text-foreground">{conversions}</span>
            <span>conversões</span>
          </span>
        </div>
      </div>

      {/* Status badge + data */}
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <StatusBadge status={group.status} />
        <p className="text-xs text-muted-foreground">
          {formatDate(group.created_at)}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PeriodSelector
// ---------------------------------------------------------------------------

interface PeriodSelectorProps {
  value: Period;
  onChange: (p: Period) => void;
}

function PeriodSelector({ value, onChange }: PeriodSelectorProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Período das métricas"
      className="flex items-center rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-0.5 gap-0.5"
    >
      {PERIODS.map((p) => {
        const active = p === value;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-pressed={active}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-all duration-[120ms]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
              active
                ? "bg-[hsl(var(--surface-raised))] text-foreground shadow-sm border border-[hsl(var(--border-strong))]"
                : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-raised)/0.5)]",
            )}
          >
            {PERIOD_LABELS[p]}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StepItem({ n, children }: { n: number; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary font-mono">
        {n}
      </span>
      <span className="text-sm leading-snug">{children}</span>
    </div>
  );
}

function StatusIcon({ status }: { status: string }): JSX.Element {
  if (status === "active") {
    return <CheckCircle2 size={20} className="text-primary" />;
  }
  if (status === "pending") {
    return <Clock size={20} className="text-amber-400" />;
  }
  return <PauseCircle size={20} className="text-muted-foreground" />;
}

const statusMap: Record<string, { label: string; className: string }> = {
  active: {
    label: "Ativo",
    className: "bg-primary/10 text-primary border-primary/20",
  },
  pending: {
    label: "Pendente",
    className: "bg-amber-400/10 text-amber-400 border-amber-400/20",
  },
  paused: {
    label: "Pausado",
    className: "bg-muted text-muted-foreground border-transparent",
  },
};

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cfg = statusMap[status] ?? {
    label: status,
    className: "bg-muted text-muted-foreground border-transparent",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// CopyRoutesSection
// ---------------------------------------------------------------------------

interface CopyRoutesSectionProps {
  groups: TelegramGroup[];
}

function CopyRoutesSection({ groups }: CopyRoutesSectionProps): JSX.Element {
  const { data: routes, isLoading, error } = useTelegramCopyRoutes();
  const createRoute = useCreateTelegramCopyRoute();
  const updateRouteStatus = useUpdateTelegramCopyRouteStatus();
  const deleteRoute = useDeleteTelegramCopyRoute();
  const [sourceGroupId, setSourceGroupId] = useState(groups.find((g) => g.status === "active")?.id ?? "");
  const [destinationGroupId, setDestinationGroupId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const activeGroups = groups.filter((g) => g.status === "active");
  const selectedSource = activeGroups.find((g) => g.id === sourceGroupId) ?? null;
  const destinationOptions = activeGroups.filter((g) => g.id !== sourceGroupId);
  const routeCount = routes?.length ?? 0;

  const handleCreate = async (): Promise<void> => {
    setFormError(null);

    if (!selectedSource || !destinationGroupId) {
      setFormError("Selecione origem e destino.");
      return;
    }

    try {
      await createRoute.mutateAsync({
        sourceGroup: selectedSource,
        destinationGroupId,
      });
      setDestinationGroupId("");
    } catch (err) {
      setFormError(errorMessage(err));
    }
  };

  return (
    <section className="space-y-4 pt-2">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Rotas de cópia</h2>
          <p className="text-sm text-muted-foreground">
            Configure quais grupos enviam e recebem as mensagens convertidas pelo bot.
          </p>
        </div>
        <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-muted-foreground">
          <Copy size={13} />
          {routeCount} {routeCount === 1 ? "rota" : "rotas"}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr_auto] md:items-end">
          <div className="space-y-2">
            <label htmlFor="copy-route-source" className="label-editorial">
              Origem
            </label>
            <select
              id="copy-route-source"
              value={sourceGroupId}
              onChange={(event) => {
                setSourceGroupId(event.target.value);
                setDestinationGroupId("");
                setFormError(null);
              }}
              className={selectClassName}
            >
              <option value="" disabled>
                Selecione
              </option>
              {activeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title ?? g.telegram_chat_id}
                </option>
              ))}
            </select>
          </div>

          <div className="hidden h-9 items-center justify-center text-muted-foreground md:flex">
            <ArrowRight size={18} />
          </div>

          <div className="space-y-2">
            <label htmlFor="copy-route-destination" className="label-editorial">
              Destino
            </label>
            <select
              id="copy-route-destination"
              value={destinationGroupId}
              onChange={(event) => {
                setDestinationGroupId(event.target.value);
                setFormError(null);
              }}
              className={selectClassName}
              disabled={!sourceGroupId || destinationOptions.length === 0}
            >
              <option value="" disabled>
                Selecione
              </option>
              {destinationOptions.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title ?? g.telegram_chat_id}
                </option>
              ))}
            </select>
          </div>

          <Button
            type="button"
            onClick={() => void handleCreate()}
            disabled={createRoute.isPending || activeGroups.length < 2}
            className="md:self-end"
          >
            <Copy size={16} />
            Criar rota
          </Button>
        </div>

        {(formError || error) && (
          <p className="mt-3 text-sm text-destructive">
            {formError ?? `Erro ao carregar rotas: ${error?.message}`}
          </p>
        )}

        {activeGroups.length < 2 && (
          <p className="mt-3 text-sm text-muted-foreground">
            Conecte ao menos dois grupos ou canais para criar uma rota.
          </p>
        )}
      </div>

      <div className="space-y-2.5" aria-busy={isLoading}>
        {isLoading && (
          <>
            <div className="h-20 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]" />
            <div className="h-20 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]" />
          </>
        )}

        {!isLoading && !error && routeCount === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] px-5 py-8 text-center space-y-2">
            <p className="text-sm font-medium text-foreground">Nenhuma rota criada ainda</p>
            <p className="text-xs text-muted-foreground">
              Selecione origem e destino acima e clique em "Criar rota" para começar o repasse automático de mensagens convertidas.
            </p>
          </div>
        )}

        {!isLoading && !error && routes?.map((route) => {
          const source = groups.find((g) => g.id === route.source_group_id);
          const destination = groups.find((g) => g.id === route.destination_group_id);
          const nextStatus = route.status === "active" ? "paused" : "active";

          return (
            <div
              key={route.id}
              className="grid gap-4 rounded-xl border border-border bg-card px-5 py-4 md:grid-cols-[1fr_auto_1fr_auto] md:items-center"
            >
              <RouteGroupLabel label="Origem" group={source} />
              <ArrowRight size={18} className="hidden text-muted-foreground md:block" />
              <RouteGroupLabel label="Destino" group={destination} />

              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                <RouteStatusBadge status={route.status} />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void updateRouteStatus.mutateAsync({ id: route.id, status: nextStatus });
                  }}
                  disabled={updateRouteStatus.isPending}
                >
                  {route.status === "active" ? "Pausar" : "Ativar"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    void deleteRoute.mutateAsync(route.id);
                  }}
                  disabled={deleteRoute.isPending}
                  aria-label="Remover rota"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// RouteGroupLabel / RouteStatusBadge
// ---------------------------------------------------------------------------

function RouteGroupLabel({
  group,
  label,
}: {
  group: TelegramGroup | undefined;
  label: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <p className="label-editorial">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-foreground">
        {group?.title ?? "Grupo removido"}
      </p>
      {group && (
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
          {group.telegram_chat_id}
        </p>
      )}
    </div>
  );
}

function RouteStatusBadge({ status }: { status: TelegramCopyRoute["status"] }): JSX.Element {
  const active = status === "active";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        active
          ? "border-primary/20 bg-primary/10 text-primary"
          : "border-transparent bg-muted text-muted-foreground",
      )}
    >
      {active ? "Ativa" : "Pausada"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

const selectClassName = cn(
  "h-10 w-full rounded-md border border-[hsl(var(--border-strong))]",
  "bg-[hsl(var(--surface-raised))] px-3 text-sm text-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Não foi possível salvar a rota.";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
